import groupBy from 'lodash.groupby';
import sortBy from 'lodash.sortby';
import mapValues from 'lodash.mapvalues';
import { getStrugglingAdvancedGoalForMeasure } from '@amplify/struggling-advanced-goal';
import { parseDate } from './assessmentWindowsUtils';
import {
    MEASURE_NAMES,
    MEASURE_SCORE_BY_RECOMMENDATION_MEASURE,
} from '../../constants/assessments';
import { DIBELS8, IDEL2 } from '../../constants/applications';
import { LECTURA_SERVICE_MODE } from '../../constants/enrollment';

const getResultScore = ({ measureScores, measure, rawScore }) => {
    if (measure === MEASURE_NAMES.CP) {
        return rawScore;
    }

    const measures = MEASURE_SCORE_BY_RECOMMENDATION_MEASURE[DIBELS8];
    const RECOMMENDATION_MEASURE =
        measure in measures
            ? MEASURE_SCORE_BY_RECOMMENDATION_MEASURE[DIBELS8]
            : MEASURE_SCORE_BY_RECOMMENDATION_MEASURE[IDEL2];
    const measureScore = measureScores[RECOMMENDATION_MEASURE[measure]];

    // it was decided that we can rely on "rawScore", no need to pick "score"
    return measureScore.rawScore;
};

/**
 * Get aimline point based on result data
 *
 * @param {Object} result
 * @returns {Object} end point for MiniAimline library in the format {<date>: {Date}, <score>: {Number}}
 */
const getAimlinePoint = result => ({
    date: parseDate(result.clientModDate ?? result.createDate),
    score: getResultScore(result),
});

/**
 * Get map of aimline points by measure based on PM results
 *
 * @param {Array<Object>} pmResults array with PM results of particular measure
 * @returns {Object} map with points for MiniAimline library grouped by measure:
 *                  {<measure>: [{<date>: {Date}, <score>: {Number}}]}
 */
export const getAimlinePointsGroupedByMeasure = pmResults =>
    mapValues(groupBy(pmResults, 'measure'), results => results.map(getAimlinePoint));

/**
 * Get aimline threshold for particular measure for D8
 *
 * @param {String} measure
 * @returns {Number} measureAimlineThreshold amount of PM results enough for marking measure as on/off track
 */
export const getD8MeasureAimlineThreshold = measure => (measure === MEASURE_NAMES.ORF ? 3 : 4);

/**
 * Get aimline threshold for particular measure for Lectura
 *
 * @param {String} measure
 * @returns {Number} measureAimlineThreshold amount of PM results enough for marking measure as on/off track
 */
export const getLecturaMeasureAimlineThreshold = measure => (measure === MEASURE_NAMES.FLO ? 3 : 4);

/**
 * Get aimline start point based on last BM result.
 * If no result provided, return null.
 *
 * @param {Object} latestBMResult
 * @returns {Object} end point for MiniAimline library in the format {<date>: {Date}, <score>: {Number}}
 */
export const getAimlineStartPoint = latestBMResult =>
    latestBMResult ? getAimlinePoint(latestBMResult) : null;

// TODO: it is temporary solution - waiting until classroom reports team will be return custom goal field
export const getScore = measureCutscore => {
    const thresholds = measureCutscore.thresholds;
    if (thresholds && measureCutscore.measure === MEASURE_NAMES.CP) {
        return thresholds[0]?.scoreThreshold;
    }
    return measureCutscore.goal;
};

// DEV NOTE: apperantly for CLPL there is no field 'goal' in cutscore
// in that case getStrugglingAdvancedGoalForMeasure will return undefined
// to avoid this we will use temp solution to insert this field from thresholds
// interpretation - At Benchmark
export const formNewCutscoreForCLPL = measureCutscore => [
    {
        ...measureCutscore,
        goal: measureCutscore?.thresholds.find(item => item.interpretation === 'At Benchmark')
            ?.scoreThreshold,
    },
];
/**
 * Get aimline end point as a combination of next TOY date and a target measure score.
 * Benchmark goal (min green cutpoint) is a default goal for the measure.
 * If a custom teacher-set goal is present for that measure score, use it
 * as a next goal score.
 * Apply struggling/advanced student goal correction rules if they meet criteria.
 * If cannot calculate goal score, return null.
 *
 * @param {String} measureScoreKey measure score key, e.g. NWF-CLS for NWF
 * @param {Object} nextGoalPeriod object with information about next goal period (MOY or EOY)
 * @param {Object} latestBMResult result for measureScoreKey completed in currentPeriod (BOY or MOY)
 * @param {Object} studentCustomGoal student goal object for measure score in next goal period, may be undefined
 * @param {Object} measureCutscore cutscore object for measure score in next goal period
 * @param {Array<Object>} growthZones array of student's zones of growth for all periods and measures
 * @param {String} aapName of the app so we can deremine service mode
 *
 * @returns {Object} start point for MiniAimline library in the format {<date>: {Date}, <score>: {Number}}
 */
export const getAimlineEndPoint = (
    measureScoreKey,
    nextGoalPeriod,
    latestBMResult,
    studentCustomGoal,
    measureCutscore,
    growthZones,
    appName,
) => {
    if (!growthZones) {
        return {
            date: parseDate(nextGoalPeriod.startDate),
            score: getScore(measureCutscore),
        };
    }

    const SERVICE_MODE = appName === IDEL2 ? LECTURA_SERVICE_MODE : '';

    const measureStatus = {
        period: latestBMResult.period,
        measure: measureScoreKey,
        score: getResultScore(latestBMResult),
    };

    const cutscore =
        measureScoreKey === MEASURE_NAMES.CP
            ? formNewCutscoreForCLPL(measureCutscore)
            : [measureCutscore];

    const score = studentCustomGoal
        ? studentCustomGoal.goalValue
        : getStrugglingAdvancedGoalForMeasure(
              measureScoreKey,
              nextGoalPeriod.period,
              [measureStatus],
              cutscore,
              growthZones,
              SERVICE_MODE,
          );
    const aimlinePoint = {
        date: parseDate(nextGoalPeriod.startDate),
        score,
    };

    return score === undefined ? null : aimlinePoint;
};

/**
 * Pick aimline middle points from all measure aimline points
 *
 * @param {Object} aimlineStartPoint start point for MiniAimline library in the format {<date>: {Date}, <score>: {Number}}
 * @param {Array<Object>} aimlineMeasurePoints array with all measure aimline points
 * @param {Object} aimlineEndPoint end point for MiniAimline library
 * @param {Number} measureAimlineThreshold amount of PM results enough for marking measure as on/off track
 * @returns {Array<Object>} aimlineMiddlePoints array with middle aimline points for MiniAimline library
 */
export const getAimlineMiddlePoints = (
    aimlineStartPoint,
    aimlineMeasurePoints,
    aimlineEndPoint,
    measureAimlineThreshold,
) =>
    // sort all measure aimline points by date
    sortBy(aimlineMeasurePoints, 'date')
        // and remove points out of aimline interval
        .filter(
            aimlinePoint =>
                aimlinePoint.date >= aimlineStartPoint.date &&
                aimlinePoint.date <= aimlineEndPoint.date,
        )
        // then leave only last 3 or 4 results
        .slice(-measureAimlineThreshold);
